import { ReactChild, forwardRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Theme } from "@mui/material";

type Props = {
  children: ReactChild | ReactChild[];
  /** Fills the width of the parent */
  fullWidth?: boolean;
  /** Fills the height of the parent */
  fullHeight?: boolean;
  /** applies `flexGrow: 1` */
  grow?: boolean;
  /** applies `flexWrap: "wrap"` */
  wrapItems?: boolean;
  /** Children are spaced evenly (overrides hAlign) */
  spaceBetween?: boolean;
  /** Horizontal alignment of children */
  hAlign?: "left" | "center" | "right";
  /** Vertical alignment of children */
  vAlign?: "top" | "center" | "bottom";
  /** Padding multiplier for theme.spacing  */
  paddingLevel?: number;
  /** Margin multiplier (around outside of container) for theme.spacing  */
  marginLevel?: number;
  /** Margin multiplier for space between direct child elements */
  itemSpacing?: number;
  /** Allow style overrides */
  className?: string;
  /** Elevation adds drop-shadow effect */
  elevation?: number;
  /** Rounded border or not */
  rounded?: boolean;
  /** Sets `overflow-y` to auto. NOTE: requires parent to have a deterministic height */
  autoScrollY?: boolean;
  /** Sets `overflow-x` to auto. NOTE: requires parent to have a deterministic width */
  autoScrollX?: boolean;
} & any;

const useStyles = makeStyles(
  (theme: Theme) =>
    ({
      row: {
        display: "flex",
        position: "relative",
        padding: ({ paddingLevel }) => paddingLevel !== undefined && theme.spacing(paddingLevel),
        margin: ({ marginLevel }) => marginLevel !== undefined && theme.spacing(marginLevel),
        width: ({ fullWidth }) => fullWidth && "100%",
        height: ({ fullHeight }) => fullHeight && "100%",
        flexGrow: ({ grow }) => grow && 1,
        flexWrap: ({ wrapItems }) => wrapItems && "wrap",
        alignItems: ({ vAlign }) =>
          (vAlign === "top" && "flex-start") ||
          (vAlign === "center" && "center") ||
          (vAlign === "bottom" && "flex-end"),
        justifyContent: ({ hAlign, spaceBetween }) =>
          spaceBetween
            ? "space-between"
            : hAlign
              ? (hAlign === "left" && "flex-start") ||
                (hAlign === "center" && "center") ||
                (hAlign === "right" && "flex-end")
              : null,
        boxShadow: ({ elevation }) => elevation && theme.shadows[elevation],
        borderRadius: ({ rounded }) => rounded && 5,
        "& > *:not(:first-child)": {
          marginLeft: ({ itemSpacing }) => itemSpacing !== undefined && theme.spacing(itemSpacing),
        },
        overflowY: ({ autoScrollY }) => (autoScrollY && "auto") || null,
        overflowX: ({ autoScrollX }) => (autoScrollX && "auto") || null,
      },
    }) as any
);

const FlexRow = forwardRef(function FlexRow(
  {
    children,
    fullWidth,
    fullHeight,
    grow,
    wrapItems,
    spaceBetween,
    hAlign,
    vAlign,
    paddingLevel,
    marginLevel,
    itemSpacing,
    className = "",
    elevation = 0,
    rounded = false,
    autoScrollY,
    autoScrollX,
    ...props
  }: Props,
  ref
) {
  const classes: any = useStyles({
    fullWidth,
    fullHeight,
    grow,
    wrapItems,
    spaceBetween,
    hAlign,
    vAlign,
    paddingLevel,
    marginLevel,
    itemSpacing,
    elevation,
    rounded,
    autoScrollY,
    autoScrollX,
  });
  return (
    <div className={`${classes.row} ${className}`} {...props} ref={ref}>
      {children}
    </div>
  );
});

export default FlexRow;
